import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Bell,
  BarChart3,
  PieChart,
  Activity,
  Users,
  Lock,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  ChevronRight,
  Info,
  Megaphone,
  Target,
  Zap,
  Globe,
  Server,
  Cpu,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface ContractStatus {
  deployed: boolean;
  address?: string;
  network?: string;
  blockExplorer?: string;
}

interface ChainData {
  totalSupply: string;
  circulatingSupply: string;
  totalStaked: string;
  rewardPool: string;
  holders: number;
  nextDistribution: number;
  currentEpoch: number;
}

export default function TokenMetrics() {
  const [activeTab, setActiveTab] = useState<'status' | 'contracts' | 'deployment'>('status');
  const [isLoading, setIsLoading] = useState(false);
  
  // Contract deployment status - REAL STATUS
  const [tokenContract, setTokenContract] = useState<ContractStatus>({ deployed: false });
  const [stakingContract, setStakingContract] = useState<ContractStatus>({ deployed: false });
  const [icoContract, setIcoContract] = useState<ContractStatus>({ deployed: false });
  const [vestingContract, setVestingContract] = useState<ContractStatus>({ deployed: false });
  
  // Chain data - Will be populated from actual blockchain
  const [chainData, setChainData] = useState<ChainData | null>(null);

  // Check for deployed contracts on component mount
  useEffect(() => {
    checkContractStatus();
  }, []);

  const checkContractStatus = async () => {
    setIsLoading(true);
    try {
      // In production, this would query the actual blockchain
      // For now, we check localStorage for deployment addresses
      const savedContracts = localStorage.getItem('hbx_contracts');
      if (savedContracts) {
        const contracts = JSON.parse(savedContracts);
        if (contracts.token) {
          setTokenContract({ deployed: true, address: contracts.token, network: contracts.network });
        }
        if (contracts.staking) {
          setStakingContract({ deployed: true, address: contracts.staking, network: contracts.network });
        }
        if (contracts.ico) {
          setIcoContract({ deployed: true, address: contracts.ico, network: contracts.network });
        }
        if (contracts.vesting) {
          setVestingContract({ deployed: true, address: contracts.vesting, network: contracts.network });
        }
      }
    } catch (error) {
      console.error('Error checking contract status:', error);
    }
    setIsLoading(false);
  };

  const ContractStatusCard = ({ 
    name, 
    status, 
    description 
  }: { 
    name: string; 
    status: ContractStatus; 
    description: string;
  }) => (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-text-primary">{name}</h3>
          <p className="text-sm text-text-tertiary mt-1">{description}</p>
        </div>
        {status.deployed ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
      </div>
      
      {status.deployed ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary">Address:</span>
            <code className="text-primary font-mono text-xs">
              {status.address?.slice(0, 6)}...{status.address?.slice(-4)}
            </code>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary">Network:</span>
            <span className="text-text-secondary">{status.network}</span>
          </div>
          <a 
            href={`${status.blockExplorer}/address/${status.address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-primary hover:underline mt-2"
          >
            View on Explorer <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ) : (
        <div className="bg-red-500/10 text-red-400 text-sm rounded-lg p-3 mt-2">
          Not deployed yet
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Coins className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">HBX Token Infrastructure</h1>
            <p className="text-text-secondary text-sm">Smart contract deployment and blockchain status</p>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium text-amber-500">Pre-Deployment Status</div>
          <p className="text-sm text-text-secondary mt-1">
            HBX contracts have not been deployed to any blockchain yet. All data shown will be real once contracts are deployed.
            No simulated or fake data is displayed.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-background-tertiary pb-2">
        {[
          { id: 'status', label: 'Contract Status', icon: Activity },
          { id: 'contracts', label: 'Smart Contracts', icon: Server },
          { id: 'deployment', label: 'Deployment Guide', icon: Zap },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg transition-all",
              activeTab === tab.id
                ? "bg-primary text-background-primary font-medium"
                : "text-text-secondary hover:bg-background-secondary"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="space-y-6">
          {/* Contract Status Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ContractStatusCard
              name="HBX Token (ERC-20)"
              status={tokenContract}
              description="Main HBX utility token - 1B max supply"
            />
            <ContractStatusCard
              name="HBX Staking Pool"
              status={stakingContract}
              description="Monthly revenue distribution to stakers"
            />
            <ContractStatusCard
              name="ICO/Token Sale"
              status={icoContract}
              description="Public token sale contract"
            />
            <ContractStatusCard
              name="Vesting Contract"
              status={vestingContract}
              description="Team & advisor token vesting"
            />
          </div>

          {/* On-Chain Data Section */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              On-Chain Metrics
            </h3>
            
            {!tokenContract.deployed ? (
              <div className="text-center py-12">
                <XCircle className="h-12 w-12 text-text-tertiary mx-auto mb-4" />
                <p className="text-text-secondary">No blockchain data available</p>
                <p className="text-sm text-text-tertiary mt-2">Deploy contracts to see real metrics</p>
              </div>
            ) : chainData ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-background-secondary rounded-lg p-4">
                  <div className="text-xs text-text-tertiary">Total Supply</div>
                  <div className="text-xl font-bold text-text-primary">{chainData.totalSupply}</div>
                </div>
                <div className="bg-background-secondary rounded-lg p-4">
                  <div className="text-xs text-text-tertiary">Total Staked</div>
                  <div className="text-xl font-bold text-text-primary">{chainData.totalStaked}</div>
                </div>
                <div className="bg-background-secondary rounded-lg p-4">
                  <div className="text-xs text-text-tertiary">Reward Pool</div>
                  <div className="text-xl font-bold text-text-primary">{chainData.rewardPool}</div>
                </div>
                <div className="bg-background-secondary rounded-lg p-4">
                  <div className="text-xs text-text-tertiary">Holders</div>
                  <div className="text-xl font-bold text-text-primary">{chainData.holders}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-text-secondary">Loading chain data...</span>
              </div>
            )}
          </div>

          {/* Tokenomics Overview */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-primary" />
              Tokenomics (Planned Allocation)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Staking Rewards', percent: 30, amount: '300M HBX', color: '#00D4AA' },
                { label: 'Platform Revenue', percent: 20, amount: '200M HBX', color: '#3B82F6' },
                { label: 'Team & Dev', percent: 15, amount: '150M HBX', color: '#8B5CF6' },
                { label: 'Liquidity', percent: 15, amount: '150M HBX', color: '#F59E0B' },
                { label: 'Marketing', percent: 10, amount: '100M HBX', color: '#EF4444' },
                { label: 'Community', percent: 10, amount: '100M HBX', color: '#10B981' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-secondary rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-text-secondary">{item.label}</span>
                  </div>
                  <div className="text-lg font-bold text-text-primary">{item.percent}%</div>
                  <div className="text-xs text-text-tertiary">{item.amount}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <div className="space-y-6">
          {/* Contract Files */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Available Smart Contracts</h3>
            
            <div className="space-y-4">
              <div className="bg-background-secondary rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-text-primary">HBXToken.sol</h4>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Ready</span>
                </div>
                <p className="text-sm text-text-tertiary mb-3">
                  ERC-20 token with built-in staking, tiered fee discounts, and revenue sharing.
                </p>
                <div className="text-xs text-text-tertiary space-y-1">
                  <div>• Max Supply: 1,000,000,000 HBX</div>
                  <div>• Features: Burnable, Permit (gasless approvals), AccessControl</div>
                  <div>• Tiers: Bronze, Silver, Gold, Platinum, Diamond</div>
                </div>
              </div>

              <div className="bg-background-secondary rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-text-primary">HBXStaking.sol</h4>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Ready</span>
                </div>
                <p className="text-sm text-text-tertiary mb-3">
                  Monthly revenue distribution pool with lock period multipliers.
                </p>
                <div className="text-xs text-text-tertiary space-y-1">
                  <div>• Lock periods: Flexible, 1mo, 3mo, 6mo, 1yr</div>
                  <div>• Multipliers: 1x to 2x based on lock</div>
                  <div>• Revenue sources tracked separately</div>
                </div>
              </div>

              <div className="bg-background-secondary rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-text-primary">HBXTokenSale.sol</h4>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Ready</span>
                </div>
                <p className="text-sm text-text-tertiary mb-3">
                  ICO/IEO contract for public token sale with whitelist and vesting.
                </p>
                <div className="text-xs text-text-tertiary space-y-1">
                  <div>• Phases: Private Sale, Public Sale, Ended</div>
                  <div>• Whitelist support with custom allocations</div>
                  <div>• Automatic vesting for large purchases (100k+ HBX)</div>
                  <div>• Referral system with 5% bonus</div>
                  <div>• Soft cap and hard cap enforcement</div>
                </div>
              </div>

              <div className="bg-background-secondary rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-text-primary">HBXVesting.sol</h4>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Ready</span>
                </div>
                <p className="text-sm text-text-tertiary mb-3">
                  Token vesting contract for team, advisors, and strategic partners.
                </p>
                <div className="text-xs text-text-tertiary space-y-1">
                  <div>• Team: 4yr vesting, 1yr cliff (revocable)</div>
                  <div>• Advisors: 2yr vesting, 6mo cliff</div>
                  <div>• Partners: 1yr vesting, 3mo cliff</div>
                  <div>• Custom vesting schedules supported</div>
                  <div>• Batch grant creation</div>
                </div>
              </div>
            </div>
          </div>

          {/* Layer 1 Discussion */}
          <div className="card p-6 border-amber-500/30">
            <h3 className="text-lg font-semibold text-amber-500 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              About Layer 1 Blockchain
            </h3>
            <p className="text-text-secondary mb-4">
              The current contracts are ERC-20 tokens designed to run on existing EVM chains (Ethereum, BSC, Polygon, etc.).
              This is NOT a Layer 1 blockchain.
            </p>
            <div className="bg-background-secondary rounded-lg p-4 mb-4">
              <h4 className="font-medium text-text-primary mb-2">Options for True Layer 1:</h4>
              <div className="text-sm text-text-tertiary space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-primary">1.</span>
                  <span><strong>Cosmos SDK</strong> - Build sovereign chain in 3-6 months, HBX as native gas token</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary">2.</span>
                  <span><strong>Avalanche Subnet</strong> - Custom subnet with HBX as gas, faster deployment</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary">3.</span>
                  <span><strong>Substrate/Polkadot</strong> - Build parachain with custom tokenomics</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary">4.</span>
                  <span><strong>Deploy as ERC-20</strong> - Ready NOW, can migrate to L1 later</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-text-tertiary">
              Recommendation: Deploy as ERC-20 for IEO/ICO now. Plan L1 migration for Phase 2 after raising funds.
            </p>
          </div>
        </div>
      )}

      {/* Deployment Tab */}
      {activeTab === 'deployment' && (
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Deployment Checklist</h3>
            
            <div className="space-y-4">
              {[
                { step: 1, title: 'Set up deployment wallet', desc: 'Create secure multi-sig wallet for deployment', done: false },
                { step: 2, title: 'Fund deployment wallet', desc: 'Add ETH/BNB for gas fees', done: false },
                { step: 3, title: 'Deploy to testnet first', desc: 'Sepolia, Goerli, or BSC Testnet', done: false },
                { step: 4, title: 'Security audit', desc: 'Get contracts audited before mainnet', done: false },
                { step: 5, title: 'Deploy HBXToken', desc: 'Deploy main token contract', done: false },
                { step: 6, title: 'Deploy HBXStaking', desc: 'Deploy staking pool contract', done: false },
                { step: 7, title: 'Deploy ICO contract', desc: 'Deploy token sale contract', done: false },
                { step: 8, title: 'Initial distribution', desc: 'Mint and distribute initial tokens', done: false },
                { step: 9, title: 'Verify contracts', desc: 'Verify source code on block explorer', done: false },
                { step: 10, title: 'Launch IEO/ICO', desc: 'Open public token sale', done: false },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-3 bg-background-secondary rounded-lg">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                    item.done ? "bg-green-500" : "bg-background-tertiary"
                  )}>
                    {item.done ? (
                      <CheckCircle2 className="h-4 w-4 text-white" />
                    ) : (
                      <span className="text-sm text-text-tertiary">{item.step}</span>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">{item.title}</div>
                    <div className="text-sm text-text-tertiary">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Network Selection */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Recommended Networks</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Ethereum Mainnet', chainId: 1, pros: 'Most trusted, highest liquidity', cons: 'High gas fees' },
                { name: 'BNB Smart Chain', chainId: 56, pros: 'Low fees, large user base', cons: 'More centralized' },
                { name: 'Polygon', chainId: 137, pros: 'Very low fees, Ethereum security', cons: 'Less liquidity' },
                { name: 'Arbitrum', chainId: 42161, pros: 'Low fees, Ethereum security', cons: 'Newer ecosystem' },
              ].map((network) => (
                <div key={network.chainId} className="bg-background-secondary rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-text-primary">{network.name}</h4>
                    <span className="text-xs text-text-tertiary">Chain {network.chainId}</span>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="text-green-400">✓ {network.pros}</div>
                    <div className="text-amber-400">⚠ {network.cons}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Command Reference */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Deployment Commands</h3>
            <div className="bg-background-tertiary rounded-lg p-4 font-mono text-sm">
              <div className="text-text-tertiary"># Install dependencies</div>
              <div className="text-primary mb-2">cd contracts && npm install</div>
              
              <div className="text-text-tertiary"># Compile contracts</div>
              <div className="text-primary mb-2">npx hardhat compile</div>
              
              <div className="text-text-tertiary"># Deploy to testnet</div>
              <div className="text-primary mb-2">npx hardhat run scripts/deploy.ts --network sepolia</div>
              
              <div className="text-text-tertiary"># Verify on Etherscan</div>
              <div className="text-primary">npx hardhat verify --network sepolia CONTRACT_ADDRESS</div>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
        <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium text-text-primary">Real Data Only</div>
          <p className="text-sm text-text-secondary mt-1">
            This dashboard displays only real blockchain data. All metrics will populate automatically once contracts are deployed and connected.
            No simulated or placeholder values are used.
          </p>
        </div>
      </div>
    </div>
  );
}
